/**
 * Diagnostics page — every customer-facing URL in one place so the admin
 * can verify each flow in ~30 seconds (instead of doing a fake purchase
 * every time we need to test).
 *
 * Each link uses REAL sample data picked from the brand:
 *   • a real customer's dashboard token
 *   • a real event slug
 *   • a real paid order token (for the success/short-link pages)
 *
 * Cards show "missing_data" with an actionable hint when there isn't yet
 * sample data of the right type (e.g. no events created yet).
 */
import React, { useEffect, useState } from 'react';
import { adminApi } from '../../services/api';
import { PageTooltip } from '../components/TooltipSystem';
import { toast } from 'sonner';
import {
  ExternalLink, Copy, CheckCircle2, AlertCircle, Globe, Award, Ticket,
  Gift, Link2, RefreshCw, ShieldCheck, Activity, XCircle, Loader2,
} from 'lucide-react';

const GROUP_ICONS = { Globe, Award, Ticket, Gift };

export default function AdminDiagnostics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copiedKey, setCopiedKey] = useState(null);
  const [health, setHealth] = useState(null);       // { results: [...], healthy, broken, total }
  const [checking, setChecking] = useState(false);

  const load = () => {
    setLoading(true);
    setHealth(null);  // Stale after data refresh — invite the user to re-check.
    adminApi.get('/admin/diagnostics/customer-links')
      .then((r) => setData(r.data))
      .catch((err) => toast.error(err?.response?.data?.detail || 'Errore di caricamento'))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const runHealthCheck = async () => {
    setChecking(true);
    try {
      const r = await adminApi.post('/admin/diagnostics/health-check');
      setHealth(r.data);
      const { healthy = 0, broken = 0 } = r.data || {};
      if (broken === 0) {
        toast.success(`Tutti i ${healthy} link rispondono correttamente!`);
      } else {
        toast.error(`${broken} link su ${healthy + broken} non rispondono`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Errore durante il check');
    } finally {
      setChecking(false);
    }
  };

  /** Map url → result for quick badge lookup. */
  const healthByUrl = React.useMemo(() => {
    if (!health?.results) return {};
    const m = {};
    health.results.forEach((r) => { m[r.url] = r; });
    return m;
  }, [health]);

  const handleCopy = async (url, key) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopiedKey(key);
      toast.success('URL copiato');
      setTimeout(() => setCopiedKey(null), 1500);
    } catch {
      toast.error('Impossibile copiare');
    }
  };

  if (loading && !data) {
    return (
      <div className="p-6 md:p-8 lg:p-10 flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-[#D44E20] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (!data) return null;

  const flatCount = (data.groups || []).reduce((acc, g) => acc + (g.links?.length || 0), 0);
  const missingCount = (data.groups || []).reduce(
    (acc, g) => acc + (g.links || []).filter((l) => l.status === 'missing_data').length,
    0,
  );

  return (
    <div className="p-6 md:p-8 lg:p-10" data-testid="admin-diagnostics-page">
      <PageTooltip page="diagnostics" />
      <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 text-xs font-semibold mb-3">
            <ShieldCheck size={12} />
            Diagnostica link
          </div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-[#1A1A1A]">Test rapido di tutti i flussi cliente</h1>
          <p className="text-[#64748B] text-sm mt-1 max-w-2xl">
            Apri qualsiasi link qui sotto e verifica che la pagina si carichi correttamente.
            Ogni link è costruito con <strong>dati reali</strong> del tuo brand
            (cliente, evento, ordine pagato), così puoi controllare l'esperienza
            esatta che ricevono i tuoi utenti.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={runHealthCheck}
            disabled={checking}
            data-testid="diagnostics-healthcheck-btn"
            className="inline-flex items-center gap-2 h-9 px-4 rounded-xl bg-[#1A1A1A] text-white text-sm font-semibold hover:bg-[#333] disabled:opacity-50 transition-colors"
          >
            {checking ? <Loader2 size={13} className="animate-spin" /> : <Activity size={13} />}
            {checking ? 'Verifico…' : 'Esegui health check'}
          </button>
          <button
            onClick={load}
            data-testid="diagnostics-refresh-btn"
            className="inline-flex items-center gap-2 h-9 px-3 rounded-xl bg-white border border-slate-200 text-sm text-[#475569] hover:border-slate-300 transition-colors"
          >
            <RefreshCw size={13} />
            Aggiorna
          </button>
        </div>
      </div>

      {/* Origin banner */}
      <div
        className={`mb-6 rounded-2xl p-4 border ${
          data.public_url_pinned
            ? 'bg-emerald-50 border-emerald-200'
            : 'bg-amber-50 border-amber-200'
        }`}
        data-testid="diagnostics-origin-banner"
      >
        <div className="flex items-start gap-3">
          <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${data.public_url_pinned ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
            <Link2 size={16} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-[#1A1A1A]">
              Dominio attivo:&nbsp;
              <code className="bg-white/60 px-1.5 py-0.5 rounded text-[12px] font-mono">{data.origin}</code>
            </p>
            <p className="text-xs text-[#64748B] mt-1">
              {data.public_url_pinned
                ? 'Dominio pinnato dal pannello Brand → Info Locale. Tutte le email/SMS transazionali useranno questo URL.'
                : 'Nessun dominio pinnato — i link usano l\'host corrente. Pinna il dominio di produzione da Brand → Info Locale per email/SMS deterministiche.'}
            </p>
          </div>
        </div>
      </div>

      {/* Summary chips */}
      <div className="flex items-center gap-3 mb-6 text-xs flex-wrap">
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-slate-100 text-slate-600 font-medium">
          {flatCount} link totali
        </span>
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 font-medium">
          <CheckCircle2 size={11} /> {flatCount - missingCount} pronti
        </span>
        {missingCount > 0 && (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-50 text-amber-700 font-medium">
            <AlertCircle size={11} /> {missingCount} da configurare
          </span>
        )}
        {health && (
          <>
            <span className="text-[#94A3B8]">·</span>
            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full font-medium ${
              health.broken === 0 ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'
            }`}>
              <Activity size={11} />
              Health check: {health.healthy}/{health.total} online
            </span>
            {health.broken > 0 && (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-red-50 text-red-700 font-medium">
                <XCircle size={11} /> {health.broken} rotti
              </span>
            )}
          </>
        )}
      </div>

      {/* Groups */}
      <div className="space-y-6">
        {(data.groups || []).map((g) => {
          const Icon = GROUP_ICONS[g.icon] || Globe;
          return (
            <section key={g.id} data-testid={`diagnostics-group-${g.id}`}>
              <h2 className="text-base font-bold text-[#1A1A1A] mb-3 flex items-center gap-2">
                <span className="w-7 h-7 rounded-lg bg-[#FDECE7] text-[#D44E20] flex items-center justify-center">
                  <Icon size={14} />
                </span>
                {g.label}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {(g.links || []).map((l, idx) => (
                  <LinkCard
                    key={`${g.id}-${idx}`}
                    link={l}
                    health={l.url ? healthByUrl[l.url] : null}
                    onCopy={(url) => handleCopy(url, `${g.id}-${idx}`)}
                    copied={copiedKey === `${g.id}-${idx}`}
                  />
                ))}
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
}

function LinkCard({ link, health, onCopy, copied }) {
  const isMissing = link.status === 'missing_data';
  const hasHealth = !!health;
  const healthy = hasHealth && health.ok;
  // Top-left status dot — green when reachable, red when broken,
  // amber for missing_data, gray when no probe has run yet.
  const dotColor = isMissing
    ? 'bg-amber-400'
    : hasHealth
      ? (healthy ? 'bg-emerald-500' : 'bg-red-500')
      : 'bg-emerald-500';
  return (
    <div
      className={`rounded-2xl border p-4 transition-all ${
        isMissing
          ? 'bg-slate-50 border-dashed border-slate-200'
          : hasHealth && !healthy
            ? 'bg-red-50/40 border-red-200'
            : 'bg-white border-slate-100 hover:border-slate-200 hover:shadow-sm'
      }`}
      data-testid={`diagnostics-link-${(link.label || '').toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
    >
      <div className="flex items-start gap-3">
        <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${dotColor}`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className={`text-sm font-semibold ${isMissing ? 'text-[#64748B]' : 'text-[#1A1A1A]'}`}>
              {link.label}
            </h3>
            {hasHealth && (
              <span
                className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-bold tabular-nums ${
                  healthy
                    ? 'bg-emerald-100 text-emerald-700'
                    : 'bg-red-100 text-red-700'
                }`}
                title={health.error || `Status code: ${health.status_code ?? 'n/a'}`}
                data-testid="health-badge"
              >
                {healthy ? <CheckCircle2 size={10} /> : <XCircle size={10} />}
                {health.status_code !== null && health.status_code !== undefined
                  ? `HTTP ${health.status_code}`
                  : (health.error || 'errore')}
              </span>
            )}
          </div>
          <p className="text-xs text-[#64748B] mt-1 leading-relaxed">{link.description}</p>

          {link.sample_label && (
            <p className="text-[10px] text-[#94A3B8] mt-2 inline-flex items-center gap-1">
              <span className="w-1 h-1 rounded-full bg-[#94A3B8]" />
              {link.sample_label}
            </p>
          )}

          {!isMissing && link.url && (
            <div className="mt-3 flex items-center gap-1.5">
              <code
                className="flex-1 min-w-0 truncate bg-slate-50 border border-slate-100 rounded-lg px-2 py-1.5 text-[11px] font-mono text-slate-600"
                title={link.url}
              >
                {link.url.replace(/^https?:\/\//, '')}
              </code>
              <button
                type="button"
                onClick={() => onCopy(link.url)}
                className="h-8 w-8 rounded-lg bg-slate-50 border border-slate-100 text-slate-500 hover:text-slate-700 hover:border-slate-200 flex items-center justify-center transition-colors flex-shrink-0"
                title="Copia URL"
                data-testid="copy-url-btn"
              >
                {copied ? <CheckCircle2 size={13} className="text-emerald-600" /> : <Copy size={13} />}
              </button>
              <a
                href={link.url}
                target="_blank"
                rel="noreferrer noopener"
                className="h-8 px-3 rounded-lg bg-[#1A1A1A] text-white text-xs font-semibold hover:bg-[#333] flex items-center gap-1.5 transition-colors flex-shrink-0"
                title="Apri in nuova scheda"
                data-testid="open-url-btn"
              >
                Apri <ExternalLink size={11} />
              </a>
            </div>
          )}

          {isMissing && link.hint && (
            <div className="mt-3 p-2.5 rounded-lg bg-amber-50 border border-amber-100 text-[11px] text-amber-800 leading-relaxed">
              <AlertCircle size={11} className="inline mr-1 -mt-0.5" />
              {link.hint}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
